import {
  AuthService,
  Employee,
  EmployeeService,
  GeogroupService,
  Partner,
  PartnerService,
  TPartnerContract,
  TPartnerTypeWithHolding,
} from '@geovelo-frontends/commons';
import moment from 'moment';
import { useContext } from 'react';

import { AppContext, offersMap } from '../app/context';

const contractsOrders: { [key: string]: number } = {
  'geovelo-entreprise-holding': 1,
  'geovelo-entreprise-premium': 2,
  'geovelo-entreprise-standard': 3,
  'geovelo-entreprise-free': 4,
};

// Purely derived from data already on `Partner.contracts` (fetched by PartnerService.getPartner)
// — no separate endpoint, so no query needed, just a shared helper instead of every consumer
// re-implementing this filter/sort.
export function getCurrentContract(partner: Partner): TPartnerContract | undefined {
  const contracts = partner.contracts?.filter(({ endDateTime }) => endDateTime) ?? [];
  const now = moment();
  // A company can keep an old, higher-tier contract in its history after reverting to free (or
  // switching tier) — prefer whichever contract is actually active today over picking by tier
  // across the whole history, and only fall back to the tier-priority pick when none is active.
  const activeToday = contracts.filter(
    ({ startDateTime, endDateTime }) =>
      (!startDateTime || startDateTime.isSameOrBefore(now)) &&
      (!endDateTime || endDateTime.isSameOrAfter(now)),
  );

  return (activeToday.length > 0 ? activeToday : contracts).sort(
    (a, b) => contractsOrders[a.contractTemplate.code] - contractsOrders[b.contractTemplate.code],
  )[0];
}

export function getPartnerType(contract: TPartnerContract): TPartnerTypeWithHolding | null {
  switch (contract.contractTemplate.code) {
    case 'geovelo-entreprise-holding':
      return 'holding';
    case 'geovelo-entreprise-premium':
      return 'premium';
    case 'geovelo-entreprise-standard':
      return 'standard';
    case 'geovelo-entreprise-free':
      return 'free';
    default:
      return null;
  }
}

export default function useEmployees({
  isWebview,
  isOnboardingWebview,
  isAddCompanyWebview,
  cancellablePromise,
}: {
  cancellablePromise: <T>(p: Promise<T>) => Promise<T>;
  isAddCompanyWebview?: boolean;
  isOnboardingWebview?: boolean;
  isWebview?: boolean;
}) {
  const {
    actions: {
      setCurrentPartner,
      setPartnerType,
      setPartnerPermissions,
      setCurrentUser,
      setUserEmployee,
      setUserTeam,
      setUserSite,
      setUserTransportHabits,
      setUserCreationReasonDialogSeen,
    },
  } = useContext(AppContext);

  async function getEmployeeSubGroups(partner: Partner) {
    if (!partner.geoGroupId) {
      setUserTeam(null);
      setUserSite(null);
      return;
    }

    try {
      const geogroups = await cancellablePromise(
        GeogroupService.getUserGeogroups({ parentId: partner.geoGroupId }),
      );

      setUserTeam(geogroups.find(({ group }) => group.subGroupType === 'companyTeam') || null);

      setUserSite(
        geogroups.find(
          ({ status, group }) =>
            (status === 'manuallyJoin' || status === 'automaticallyJoin') &&
            group.subGroupType === 'companySite',
        ) || null,
      );
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
        setUserTeam(null);
        setUserSite(null);
      }
    }
  }

  async function getEmployeeTransportHabits(employee: Employee) {
    try {
      const habits = await cancellablePromise(
        EmployeeService.getTransportHabits({ employeeId: employee.id }),
      );

      setUserTransportHabits(habits);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
        setUserTransportHabits(null);
      }
    }
  }

  async function signOut() {
    await AuthService.signOut();
    setCurrentUser(null);
  }

  async function selectEmployee(employee: Employee, _partner: Partner) {
    const contract = getCurrentContract(_partner) as TPartnerContract;

    const partnerType = getPartnerType(contract);

    if (!contract || !partnerType) {
      if (!isOnboardingWebview && !isAddCompanyWebview) signOut();
      return;
    }

    if (isWebview) employee.role = 'employee';

    const partner = await cancellablePromise(
      PartnerService.getPartner({ isAdmin: employee.isAdminOrAnimator, partner: _partner }),
    );

    setCurrentPartner(partner);

    setPartnerType(partnerType);

    const { permissions } = offersMap[partnerType || 'free'];

    if (partnerType !== 'holding') {
      getEmployeeSubGroups(partner);
      getEmployeeTransportHabits(employee);
      setUserCreationReasonDialogSeen(true);
    } else {
      setUserTeam(null);
      setUserSite(null);
      setUserTransportHabits(null);
    }

    setPartnerPermissions(permissions);

    setUserEmployee(employee);
  }

  return { selectEmployee };
}
